import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import {
  availablePaymentMethods,
  isStorablePaymentMethodId,
  type PaymentMethodStrategy,
  type StorablePaymentMethodId,
} from "@/lib/paymentMethods";
import type { StoreSettingsFormValues } from "./StoreSettingsForm";

function isStorablePaymentMethodStrategy(
  method: PaymentMethodStrategy
): method is PaymentMethodStrategy & { id: StorablePaymentMethodId } {
  return isStorablePaymentMethodId(method.id);
}

export function AcceptedPaymentMethodsSection() {
  const form = useFormContext<StoreSettingsFormValues>();
  const selected = form.watch("acceptedPaymentMethods");
  const methods = availablePaymentMethods().filter(isStorablePaymentMethodStrategy);

  const toggleMethod = (methodId: StorablePaymentMethodId) => {
    const current = form.getValues("acceptedPaymentMethods");
    const isSelected = current.includes(methodId);

    if (isSelected && current.length === 1) {
      return;
    }

    const next = isSelected
      ? current.filter((id) => id !== methodId)
      : [...current, methodId];

    form.setValue("acceptedPaymentMethods", next, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div className="space-y-3 pt-6 border-t border-primary-200 dark:border-primary-800">
      <div>
        <h3 className="text-lg font-light tracking-tight text-primary-900 dark:text-white">
          Formas de pagamento
        </h3>
        <p className="text-sm font-light text-primary-600 dark:text-primary-400 mt-1">
          Escolha quais opções o cliente pode selecionar ao falar no WhatsApp.
        </p>
      </div>

      <fieldset className="space-y-2">
        <legend className="sr-only">Formas de pagamento aceitas</legend>
        {methods.map((method) => {
          const checked = selected.includes(method.id);
          const isOnlySelection = checked && selected.length === 1;

          return (
            <label
              key={method.id}
              className={cn(
                "flex items-center gap-3 border border-primary-200 dark:border-primary-800 px-4 py-3 cursor-pointer",
                isOnlySelection && "cursor-default"
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={isOnlySelection}
                onChange={() => toggleMethod(method.id)}
                className="h-4 w-4 border-primary-300 dark:border-primary-700"
              />
              <span className="text-sm font-light text-primary-900 dark:text-white">
                {method.label}
              </span>
            </label>
          );
        })}
      </fieldset>

      {form.formState.errors.acceptedPaymentMethods && (
        <p className="text-sm font-light text-red-600 dark:text-red-400" role="alert">
          {form.formState.errors.acceptedPaymentMethods.message}
        </p>
      )}
    </div>
  );
}
